import { Tag } from '@carbon/react';
import {
  EventResponse,
  TypeCurveTaskDisplayMetadata,
} from 'types/api-responses';
import {
  Bullhorn,
  TrashCan,
  Comment,
  SupportingFile,
  Accepted,
  Rejected,
  Undo,
} from 'components/icons';
import { Date } from 'components/date';

import style from './event.module.scss';

// Verdict -> Carbon tag colour. Green reads as settled-correct and red as settled-incorrect at a
// glance, which is the whole point of the badge; a withdrawn verdict is neutral, not a failure.
const VERDICT_TAG_TYPE: Record<string, 'green' | 'red' | 'gray'> = {
  correct: 'green',
  incorrect: 'red',
  unverified: 'gray',
};

const Event = ({ item }: { item: EventResponse }) => {
  let Icon: React.FunctionComponent<{ className: string }>;
  const kind = item.data.kind;
  if (kind === 'create_comment') {
    Icon = Comment;
  } else if (kind === 'create_supporting_file') {
    Icon = SupportingFile;
  } else if (kind === 'destroy_supporting_file') {
    Icon = TrashCan;
  } else if (kind === 'completion_correct') {
    Icon = Accepted;
  } else if (kind === 'completion_incorrect') {
    Icon = Rejected;
  } else if (kind === 'completion_reverted') {
    Icon = Undo;
  } else {
    Icon = Bullhorn;
  }
  return (
    <li className={style.event}>
      <div className={style.header}>
        <Icon className={style.icon} />
        <div>
          <h6>{item.createdBy}</h6>
          <Date
            className={style.date}
            date={item.createdAt}
            format={'lll'}
            showTime
          />
        </div>
      </div>

      <div className={style.content}>
        <EventContent item={item.data} />
      </div>
    </li>
  );
};

function EventContent({ item }: { item: EventResponse['data'] }) {
  if (item.kind === 'completion_incorrect') {
    return (
      <div>
        <p>Status Updated: Incorrect</p>
        <p>Reason: {item.content.reason}</p>
        {item.content.details && <p>Details: {item.content.details}</p>}
      </div>
    );
  }

  if (item.kind === 'completion_correct') {
    return (
      <div>
        <p>Status Updated: Correct</p>
      </div>
    );
  }

  if (item.kind === 'completion_reverted') {
    return (
      <div>
        <p>Status Updated: Reverted</p>
      </div>
    );
  }

  // A Type Curve task verdict or note. The backend resolves the wording, so this only lays it out:
  // the headline, the operator's reason and note, then a section per evidence group.
  const display = 'displayMetadata' in item ? item.displayMetadata : undefined;
  if (display) {
    return <TypeCurveTaskContent display={display} />;
  }

  if (typeof item.content === 'string') {
    return <div dangerouslySetInnerHTML={{ __html: item.content }} />;
  }

  return null;
}

function TypeCurveTaskContent({
  display,
}: {
  display: TypeCurveTaskDisplayMetadata;
}) {
  return (
    <div>
      <div className={style.headline}>{display.headline}</div>
      {display.reason ? <div>Reason: {display.reason}</div> : null}
      {display.note && display.note !== display.reason ? (
        <div>Note: {display.note}</div>
      ) : null}
      {display.sections.map((section) => (
        <div className={style.section} key={section.title}>
          <div className={style.sectionTitle}>{section.title}</div>
          <ul className={style.sectionItems}>
            {section.items.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </div>
      ))}
      {display.verdict ? (
        <Tag
          size="sm"
          type={VERDICT_TAG_TYPE[display.verdict.toLowerCase()] ?? 'gray'}
          className={style.verdictTag}
        >
          {`Verdict: ${display.verdict}`}
        </Tag>
      ) : null}
    </div>
  );
}

export { Event };
